import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Save, Building2, CreditCard, FileText, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpenseFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get('clone');
  const { getAuthHeaders, user } = useAuth();
  const isEdit = !!id;
  const isClone = !isEdit && !!cloneFromId;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Master lists
  const [masterLists, setMasterLists] = useState({
    submitted_by: [],
    payment_modes: [],
    payment_types: [],
    categories: []
  });

  // Form state
  const [expenseType, setExpenseType] = useState('CORPORATE_TRAINING');
  
  // Training Information
  const [clientName, setClientName] = useState('');
  const [trainingName, setTrainingName] = useState('');
  const [zohoCrmId, setZohoCrmId] = useState('');
  const [expenseId, setExpenseId] = useState('');
  const [reportingDate, setReportingDate] = useState(new Date().toISOString().split('T')[0]);
  const [submittedBy, setSubmittedBy] = useState('');
  
  // Other expense fields
  const [description, setDescription] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [category, setCategory] = useState('');
  
  // Expense heads (for training)
  const [trainerCost, setTrainerCost] = useState('0');
  const [books, setBooks] = useState('0');
  const [trainerAccommodation, setTrainerAccommodation] = useState('0');
  const [clientExpense, setClientExpense] = useState('0');
  const [trainerTravel, setTrainerTravel] = useState('0');
  const [trainerFood, setTrainerFood] = useState('0');
  const [otherExpenses, setOtherExpenses] = useState('0');
  
  // Payment info
  const [paymentType, setPaymentType] = useState('');
  const [amountToPay, setAmountToPay] = useState('');
  const [expectedPaymentDate, setExpectedPaymentDate] = useState('');
  const [modeOfPayment, setModeOfPayment] = useState('');
  const [accountDetails, setAccountDetails] = useState('');

  // Financial Year assignment
  const [financialYear, setFinancialYear] = useState(() => {
    const now = new Date();
    const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${yr}-${String(yr + 1).slice(2)}`;
  });

  useEffect(() => {
    fetchMasterLists();
  }, []);

  useEffect(() => {
    if (isEdit && Object.keys(masterLists.submitted_by).length > 0) {
      fetchExpense();
    } else if (isClone && Object.keys(masterLists.submitted_by).length > 0) {
      fetchExpenseForClone();
    } else if (!isEdit && !isClone) {
      setLoading(false);
    }
  }, [id, cloneFromId, masterLists]);

  const fetchMasterLists = async () => {
    try {
      const response = await axios.get(`${API}/settings/master-lists`, {
        headers: getAuthHeaders()
      });
      const listsMap = {};
      response.data.forEach(list => {
        listsMap[list.list_type] = list.items.filter(item => item.is_active);
      });
      setMasterLists(listsMap);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch master lists:', error);
      toast.error('Failed to load form options');
    }
  };

  const fetchExpense = async () => {
    try {
      const response = await axios.get(`${API}/expenses/${id}`, {
        headers: getAuthHeaders()
      });
      const expense = response.data;
      
      setExpenseType(expense.expense_type || 'CORPORATE_TRAINING');
      setClientName(expense.company_name || '');
      setTrainingName(expense.training_name || '');
      setZohoCrmId(expense.corporate_project_id_new || expense.corporate_project_id || '');
      setExpenseId(expense.zoho_form_entry_id || '');
      setReportingDate(expense.reporting_date || '');
      setSubmittedBy(expense.submitted_by || '');
      setDescription(expense.description || '');
      setVendorName(expense.vendor_name || '');
      setInvoiceNumber(expense.invoice_number || '');
      setCategory(expense.category || '');
      
      // Set financial year
      if (expense.financial_year) {
        setFinancialYear(expense.financial_year);
      }
      
      if (expense.expense_heads) {
        setTrainerCost(expense.expense_heads.trainer_cost?.toString() || '0');
        setBooks(expense.expense_heads.books?.toString() || '0');
        setTrainerAccommodation(expense.expense_heads.trainer_accommodation?.toString() || '0');
        setClientExpense(expense.expense_heads.client_expense?.toString() || '0');
        setTrainerTravel(expense.expense_heads.trainer_travel?.toString() || '0');
        setTrainerFood(expense.expense_heads.trainer_food?.toString() || '0');
        setOtherExpenses(expense.expense_heads.other_expenses?.toString() || '0');
      }
      
      if (expense.payment_info) {
        setPaymentType(expense.payment_info.payment_type || '');
        setAmountToPay(expense.payment_info.amount_to_pay_inr?.toString() || '');
        setExpectedPaymentDate(expense.payment_info.expected_payment_date || '');
        setModeOfPayment(expense.payment_info.mode_of_payment || '');
        setAccountDetails(expense.payment_info.account_details || '');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch expense:', error);
      toast.error('Failed to load expense');
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseForClone = async () => {
    try {
      const response = await axios.get(`${API}/expenses/${cloneFromId}`, {
        headers: getAuthHeaders()
      });
      const expense = response.data;
      // Force HR type for clone (clone feature is HR-only)
      setExpenseType('HR_EXPENSE');
      // Copy descriptive fields
      setDescription(expense.description || '');
      setVendorName(expense.vendor_name || '');
      setCategory(expense.category || '');
      setSubmittedBy(expense.submitted_by || '');
      // Reset entry-specific identifiers (must be unique)
      setInvoiceNumber('');
      setExpenseId('');
      // Reset date to today so user enters fresh entry
      setReportingDate(new Date().toISOString().split('T')[0]);
      // Keep FY default to current (don't copy old FY)
      // Copy payment info as a template, but reset expected date (user will set new)
      if (expense.payment_info) {
        setPaymentType(expense.payment_info.payment_type || '');
        setAmountToPay(expense.payment_info.amount_to_pay_inr?.toString() || '');
        setModeOfPayment(expense.payment_info.mode_of_payment || '');
        setAccountDetails(expense.payment_info.account_details || '');
        setExpectedPaymentDate('');
      }
      toast.success('Cloned from previous HR expense — edit values & save as new entry');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to clone expense:', error);
      toast.error('Failed to load source expense for cloning');
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const values = [trainerCost, books, trainerAccommodation, clientExpense, trainerTravel, trainerFood, otherExpenses];
    return values.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const isTraining = expenseType === 'CORPORATE_TRAINING';
      const totalExpenses = isTraining ? calculateTotal() : parseFloat(amountToPay) || 0;

      const payload = {
        expense_type: expenseType,
        corporate_project_id: isTraining ? zohoCrmId?.toString() : null,
        corporate_project_id_new: isTraining ? zohoCrmId?.toString() : null,
        training_name: isTraining ? trainingName : null,
        company_name: isTraining ? clientName : null,
        description: !isTraining ? description : null,
        vendor_name: vendorName || null,
        invoice_number: invoiceNumber || null,
        category: !isTraining ? category : null,
        reporting_date: reportingDate,
        submitted_by: submittedBy || null,
        zoho_form_entry_id: expenseId || null,
        financial_year: financialYear,
        total_expenses: totalExpenses,
        expense_heads: isTraining ? {
          trainer_cost: parseFloat(trainerCost) || 0,
          books: parseFloat(books) || 0,
          trainer_accommodation: parseFloat(trainerAccommodation) || 0,
          client_expense: parseFloat(clientExpense) || 0,
          trainer_travel: parseFloat(trainerTravel) || 0,
          trainer_food: parseFloat(trainerFood) || 0,
          other_expenses: parseFloat(otherExpenses) || 0
        } : null,
        payment_info: {
          payment_type: paymentType || null,
          amount_to_pay_inr: parseFloat(amountToPay) || totalExpenses,
          expected_payment_date: expectedPaymentDate || null,
          mode_of_payment: modeOfPayment || null,
          account_details: accountDetails || null,
          payment_status: 'PENDING'
        }
      };

      if (isEdit) {
        await axios.put(`${API}/expenses/${id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`${API}/expenses`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Expense created successfully');
      }

      navigate('/expenses');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to save expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
      </div>
    );
  }

  const isTraining = expenseType === 'CORPORATE_TRAINING';
  const totalExpenses = calculateTotal();

  return (
    <div className="space-y-6 max-w-4xl" data-testid="expense-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/expenses')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950">
            {isEdit ? 'Edit Expense' : (isClone ? 'Clone HR Expense' : 'Add New Expense')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isEdit ? 'Update expense details' : (isClone ? 'Pre-filled from a previous HR expense — review & adjust values before saving as a new entry' : 'Create a new expense entry manually')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Expense Type Selection */}
        <Card className="border-slate-200 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading text-indigo-950">Expense Type</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={expenseType} onValueChange={setExpenseType} disabled={isEdit}>
              <SelectTrigger className="w-full max-w-xs" data-testid="expense-type-select">
                <SelectValue placeholder="Select expense type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CORPORATE_TRAINING">Corporate Training</SelectItem>
                <SelectItem value="HR_EXPENSE">HR Expense</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Training Information - Only for Corporate Training */}
        {isTraining && (
          <Card className="border-slate-200 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Training Information
              </CardTitle>
              <CardDescription>Details about the corporate training</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientName">Client Name *</Label>
                  <Input
                    id="clientName"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="e.g., Coworkzen"
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trainingName">Training Name *</Label>
                  <Input
                    id="trainingName"
                    value={trainingName}
                    onChange={(e) => setTrainingName(e.target.value)}
                    placeholder="e.g., Effective Communication"
                    required
                    data-testid="training-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zohoCrmId">Zoho CRM ID *</Label>
                  <Input
                    id="zohoCrmId"
                    type="number"
                    value={zohoCrmId}
                    onChange={(e) => setZohoCrmId(e.target.value)}
                    placeholder="e.g., 88"
                    required
                    data-testid="zoho-crm-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expenseId">Expense ID (Reference)</Label>
                  <Input
                    id="expenseId"
                    value={expenseId}
                    onChange={(e) => setExpenseId(e.target.value)}
                    placeholder="e.g., CEP-216"
                    data-testid="expense-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reportingDate">Date of Report *</Label>
                  <Input
                    id="reportingDate"
                    type="date"
                    value={reportingDate}
                    onChange={(e) => setReportingDate(e.target.value)}
                    required
                    data-testid="reporting-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="financialYear">Financial Year</Label>
                  <Select value={financialYear} onValueChange={setFinancialYear}>
                    <SelectTrigger data-testid="financial-year-select">
                      <SelectValue placeholder="Select FY" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const now = new Date();
                        const currentYr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
                        const years = [];
                        for (let y = currentYr + 1; y >= currentYr - 4; y--) {
                          years.push(`${y}-${String(y + 1).slice(2)}`);
                        }
                        return years.map(fy => (
                          <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="submittedBy">Submitted By *</Label>
                  {masterLists.submitted_by?.length > 0 ? (
                    <Select value={submittedBy} onValueChange={setSubmittedBy}>
                      <SelectTrigger data-testid="submitted-by-select">
                        <SelectValue placeholder="Select person" />
                      </SelectTrigger>
                      <SelectContent>
                        {masterLists.submitted_by.map((item) => (
                          <SelectItem key={item.value} value={item.label}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="submittedBy"
                      value={submittedBy}
                      onChange={(e) => setSubmittedBy(e.target.value)}
                      placeholder="Enter name"
                      data-testid="submitted-by-input"
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* HR Expense Form */}
        {!isTraining && (
          <Card className="border-slate-200 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                HR Expense Details
              </CardTitle>
              <CardDescription>Enter the HR expense information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of expense"
                    required
                    data-testid="description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  {masterLists.categories?.length > 0 ? (
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {masterLists.categories.map((item) => (
                          <SelectItem key={item.value} value={item.label}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      placeholder="e.g., Recruitment, Training, Benefits"
                      required
                      data-testid="category-input"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendorName">Payee Name *</Label>
                  <Input
                    id="vendorName"
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                    placeholder="Name of payee"
                    required
                    data-testid="payee-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invoiceNumber">Invoice Number</Label>
                  <Input
                    id="invoiceNumber"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="e.g., INV-2026-001 (optional)"
                    data-testid="invoice-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reportingDate">Date *</Label>
                  <Input
                    id="reportingDate"
                    type="date"
                    value={reportingDate}
                    onChange={(e) => setReportingDate(e.target.value)}
                    required
                    data-testid="reporting-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="financialYear">Financial Year</Label>
                  <Select value={financialYear} onValueChange={setFinancialYear}>
                    <SelectTrigger data-testid="financial-year-select-hr">
                      <SelectValue placeholder="Select FY" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const now = new Date();
                        const currentYr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
                        const years = [];
                        for (let y = currentYr + 1; y >= currentYr - 4; y--) {
                          years.push(`${y}-${String(y + 1).slice(2)}`);
                        }
                        return years.map(fy => (
                          <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="submittedBy">Submitted By *</Label>
                  {masterLists.submitted_by?.length > 0 ? (
                    <Select value={submittedBy} onValueChange={setSubmittedBy}>
                      <SelectTrigger data-testid="submitted-by-select">
                        <SelectValue placeholder="Select person" />
                      </SelectTrigger>
                      <SelectContent>
                        {masterLists.submitted_by.map((item) => (
                          <SelectItem key={item.value} value={item.label}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="submittedBy"
                      value={submittedBy}
                      onChange={(e) => setSubmittedBy(e.target.value)}
                      placeholder="Enter name"
                      required
                      data-testid="submitted-by-input"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amountToPay">Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="amountToPay"
                      type="number"
                      min="0"
                      value={amountToPay}
                      onChange={(e) => setAmountToPay(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      required
                      data-testid="amount-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modeOfPayment">Mode of Payment *</Label>
                  <Select value={modeOfPayment} onValueChange={setModeOfPayment}>
                    <SelectTrigger data-testid="mode-of-payment-select">
                      <SelectValue placeholder="Select payment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {masterLists.payment_modes?.map((item) => (
                        <SelectItem key={item.value} value={item.label}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expectedPaymentDate">Expected Payment Date</Label>
                  <Input
                    id="expectedPaymentDate"
                    type="date"
                    value={expectedPaymentDate}
                    onChange={(e) => setExpectedPaymentDate(e.target.value)}
                    data-testid="expected-payment-date-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="accountDetails">Account Details</Label>
                  <Textarea
                    id="accountDetails"
                    value={accountDetails}
                    onChange={(e) => setAccountDetails(e.target.value)}
                    placeholder="Bank details, account number, IFSC code, UPI ID, etc."
                    rows={3}
                    data-testid="account-details-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expense Breakdown - Only for Corporate Training */}
        {isTraining && (
          <Card className="border-slate-200 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Expense Breakdown
              </CardTitle>
              <CardDescription>Enter amount for each expense head</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="trainerCost">Trainer Cost</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="trainerCost"
                      type="number"
                      min="0"
                      value={trainerCost}
                      onChange={(e) => setTrainerCost(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      data-testid="trainer-cost-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="books">Books</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="books"
                      type="number"
                      min="0"
                      value={books}
                      onChange={(e) => setBooks(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      data-testid="books-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trainerAccommodation">Trainer Accommodation</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="trainerAccommodation"
                      type="number"
                      min="0"
                      value={trainerAccommodation}
                      onChange={(e) => setTrainerAccommodation(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      data-testid="trainer-accommodation-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientExpense">Client Expense</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="clientExpense"
                      type="number"
                      min="0"
                      value={clientExpense}
                      onChange={(e) => setClientExpense(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      data-testid="client-expense-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trainerTravel">Travel</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="trainerTravel"
                      type="number"
                      min="0"
                      value={trainerTravel}
                      onChange={(e) => setTrainerTravel(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      data-testid="trainer-travel-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trainerFood">Trainer Food</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="trainerFood"
                      type="number"
                      min="0"
                      value={trainerFood}
                      onChange={(e) => setTrainerFood(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      data-testid="trainer-food-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otherExpenses">Other Expenses</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="otherExpenses"
                      type="number"
                      min="0"
                      value={otherExpenses}
                      onChange={(e) => setOtherExpenses(e.target.value)}
                      className="pl-8"
                      placeholder="0"
                      data-testid="other-expenses-input"
                    />
                  </div>
                </div>
              </div>
              
              <Separator className="my-4" />
              
              <div className="flex justify-between items-center bg-indigo-50 p-4 rounded-lg">
                <span className="text-lg font-semibold text-indigo-950">Total Expenses</span>
                <span className="text-2xl font-bold font-mono text-indigo-950" data-testid="total-expenses">
                  {formatCurrency(totalExpenses)}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Information - Only for Corporate Training */}
        {isTraining && (
          <Card className="border-slate-200 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentType">Payment Type</Label>
                  <Select value={paymentType} onValueChange={setPaymentType}>
                    <SelectTrigger data-testid="payment-type-select">
                      <SelectValue placeholder="Select payment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {masterLists.payment_types?.map((item) => (
                        <SelectItem key={item.value} value={item.label}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amountToPay">Amount in INR *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      id="amountToPay"
                      type="number"
                      min="0"
                      value={amountToPay}
                      onChange={(e) => setAmountToPay(e.target.value)}
                      className="pl-8"
                      placeholder={totalExpenses.toString()}
                      data-testid="amount-to-pay-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modeOfPayment">Mode of Payment</Label>
                  <Select value={modeOfPayment} onValueChange={setModeOfPayment}>
                    <SelectTrigger data-testid="mode-of-payment-select">
                      <SelectValue placeholder="Select payment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {masterLists.payment_modes?.map((item) => (
                        <SelectItem key={item.value} value={item.label}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expectedPaymentDate">Expected Payment Date</Label>
                  <Input
                    id="expectedPaymentDate"
                    type="date"
                    value={expectedPaymentDate}
                    onChange={(e) => setExpectedPaymentDate(e.target.value)}
                    data-testid="expected-payment-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountDetails">Account Details</Label>
                <Textarea
                  id="accountDetails"
                  value={accountDetails}
                  onChange={(e) => setAccountDetails(e.target.value)}
                  placeholder="Bank details, account number, IFSC code, etc."
                  rows={3}
                  data-testid="account-details-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/expenses')}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={saving}
            className="bg-indigo-950 hover:bg-indigo-900"
            data-testid="save-expense-btn"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEdit ? 'Update Expense' : 'Save Expense'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseFormPage;
